import { useEffect, useState, useRef } from 'react'
import { Moon, Sun, Monitor, User, LogOut, Users } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/authStore'
import { useThemeStore } from '@/stores/themeStore'
import { useViewModeStore } from '@/stores/viewModeStore'
import api from '@/lib/api'

interface HeaderProps {
  title?: string
}

export function Header({ title }: HeaderProps) {
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const { theme, setTheme } = useThemeStore()
  const { viewMode, setViewMode, household, setHousehold, isLoadingHousehold, setIsLoadingHousehold } = useViewModeStore()
  const navigate = useNavigate()

  // Fetch household data once per user. Without the guard, a user with no
  // household (404 → null) re-triggered this effect in an endless loop.
  const fetchedHouseholdFor = useRef<string | null>(null)
  useEffect(() => {
    async function fetchHousehold() {
      if (household !== null || isLoadingHousehold) return
      const key = user?.email ?? ''
      if (fetchedHouseholdFor.current === key) return
      fetchedHouseholdFor.current = key
      setIsLoadingHousehold(true)
      try {
        const response = await api.get('/household')
        setHousehold(response.data)
      } catch {
        setHousehold(null)
      } finally {
        setIsLoadingHousehold(false)
      }
    }
    if (user) {
      fetchHousehold()
    }
  }, [user, household, isLoadingHousehold, setHousehold, setIsLoadingHousehold])

  const getInitials = (email: string) => {
    return email.charAt(0).toUpperCase()
  }

  const hasHousehold = !!household
  const [showViewLabel, setShowViewLabel] = useState(false)

  const handleViewModeToggle = (mode: 'individual' | 'household') => {
    if (mode === viewMode) return
    setViewMode(mode)
    setShowViewLabel(true)
    setTimeout(() => setShowViewLabel(false), 1500)
  }

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-gray-200 bg-white/80 px-6 backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/80">
      <div className="flex items-center gap-4">
        {title && (
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {title}
          </h1>
        )}
      </div>

      <div className="flex items-center gap-3">
        {/* Household/Individual view toggle */}
        {hasHousehold && (
          <div className="flex items-center gap-2">
            {/* Animated label */}
            <div
              className={cn(
                'text-sm font-medium transition-all duration-300 overflow-hidden whitespace-nowrap',
                showViewLabel
                  ? 'max-w-32 opacity-100'
                  : 'max-w-0 opacity-0'
              )}
            >
              <span className="text-gray-600 dark:text-gray-300">
                {viewMode === 'household' ? 'Household view' : 'Individual view'}
              </span>
            </div>

            {/* Pill toggle */}
            <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-full p-1">
              <button
                onClick={() => handleViewModeToggle('individual')}
                className={cn(
                  'flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200',
                  viewMode === 'individual'
                    ? 'bg-white dark:bg-gray-700 shadow-sm text-primary-600 dark:text-primary-400'
                    : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                )}
                title="Individual view"
              >
                <User className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleViewModeToggle('household')}
                className={cn(
                  'flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200',
                  viewMode === 'household'
                    ? 'bg-white dark:bg-gray-700 shadow-sm text-primary-600 dark:text-primary-400'
                    : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                )}
                title="Household view"
              >
                <Users className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Theme toggle */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-9 w-9">
              {theme === 'light' && <Sun className="h-5 w-5" />}
              {theme === 'dark' && <Moon className="h-5 w-5" />}
              {theme === 'system' && <Monitor className="h-5 w-5" />}
              <span className="sr-only">Toggle theme</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setTheme('light')}>
              <Sun className="mr-2 h-4 w-4" />
              Light
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme('dark')}>
              <Moon className="mr-2 h-4 w-4" />
              Dark
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme('system')}>
              <Monitor className="mr-2 h-4 w-4" />
              System
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User avatar with dropdown */}
        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-9 w-9 rounded-full p-0">
                <Avatar className="h-9 w-9">
                  <AvatarFallback className="bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
                    {getInitials(user.email)}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => navigate('/profile')}>
                <User className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => logout()}
                className="text-danger focus:text-danger"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  )
}
